// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { CartLineNotesStyles } from "@insite/content-library/Widgets/Cart/CartLineNotes";
import { CartLineQuantityStyles } from "@insite/content-library/Widgets/Cart/CartLineQuantity";
import X from "@insite/mobius/Icons/X";
import { LinkPresentationProps } from "@insite/mobius/Link";
import productDescriptionVariantStyles from "@pacific/App/SharedStyle/ProductDescriptionStyles";
import { constants } from "@pacific/XDL/constants";
import List from "@pacific/XDL/Icons/List";
import RequestQuote from "@pacific/XDL/Icons/RequestQuote";
import Save from "@pacific/XDL/Icons/Save";
import { createLinkIconStyles, ellipsis, mergeWithCss, xScreen } from "@pacific/XDL/Utilities";
import { css } from "styled-components";

interface CartPageStyles {
    cartActions?: {
        saveOrderLink?: LinkPresentationProps;
        addAllToListClickable?: LinkPresentationProps;
        removeAll?: LinkPresentationProps;
        requestQuote?: LinkPresentationProps;
    };
    cartLine?: {
        productImage?: ProductImageStyles;
        productBrand?: ProductBrandStyles;
        productDescription?: ProductDescriptionStyles;
        productPartNumbers?: ProductPartNumbersStyles;
        productAvailability?: ProductAvailabilityStyles;
        productPrice?: ProductPriceStyles;
        quantity?: CartLineQuantityStyles;
        totalSmallHeadingAndText?: SmallHeadingAndTextStyles;
        cartLineNotes?: CartLineNotesStyles;
        addToListLink?: LinkPresentationProps;
        removeLink?: LinkPresentationProps;
        totalSmallHeadingAndTextTable?: SmallHeadingAndTextStyles;
    };
}
const linkIconStyles: LinkPresentationProps = {
    typographyProps: {
        weight: 600,
        css: css`
            letter-spacing: 0;
            line-height: 16px;
        `,
    },
    icon: {
        iconProps: {
            size: 12,
        },
    },
};

const cartPageStyles: CartPageStyles = {
    cartActions: {
        saveOrderLink: {
            ...createLinkIconStyles(Save, linkIconStyles),
        },
        addAllToListClickable: {
            ...createLinkIconStyles(List, linkIconStyles),
        },
        removeAll: {
            ...createLinkIconStyles(X, linkIconStyles),
        },
        requestQuote: {
            ...createLinkIconStyles(RequestQuote, linkIconStyles),
        },
    },
    cartLine: {
        productImage: {
            linkWrappingImage: {
                css: css`
                    width: 100%;
                    height: 100%;
                `,
            },
            img: {
                width: 100,
                height: 88,
                css: css`
                    max-height: 100%;
                    height: auto;
                `,
                urlOptions: "t_prdsm",
            } as any,
        },
        productDescription: {
            ...mergeWithCss(productDescriptionVariantStyles.medium, {
                productDetailDescription: {
                    css: css`
                        ${ellipsis(2)}
                    `,
                },
                productDetailLink: {
                    typographyProps: {
                        css: css`
                            ${ellipsis(2)}
                        `,
                    },
                    css: css`
                        white-space: pre-wrap;
                    `,
                },
            }),
        },
        quantity: {
            editableQuantityTextField: {
                cssOverrides: {
                    inputSelect: css`
                        width: 57px;
                    `,
                },
                labelProps: {
                    css: css`
                        display: none;
                        ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                            display: block;
                        }
                        @media print {
                            display: block;
                        }
                    `,
                },
            },
        },
        addToListLink: {
            ...createLinkIconStyles(List, linkIconStyles),
        },
        removeLink: {
            ...createLinkIconStyles(X, linkIconStyles),
        },
        productPrice: {
            price: {
                priceText: {
                    variant: "h6",
                },
            },
            priceLabelText: {
                size: 12,
                weight: 500,
                css: css`
                    letter-spacing: 0;
                    line-height: 18px;
                    ${xScreen("gt", constants.cartLine.breakpoints.md)} {
                        display: none;
                    }
                    @media print {
                        display: block;
                    }
                `,
            },
        },
        totalSmallHeadingAndText: {
            text: {
                variant: "h6",
            },
            heading: {
                size: 12,
                weight: 500,
                css: css`
                    letter-spacing: 0;
                    line-height: 18px;
                    ${xScreen("gt", constants.cartLine.breakpoints.md)} {
                        display: none;
                    }
                    @media print {
                        display: block;
                    }
                `,
            },
        },
        totalSmallHeadingAndTextTable: {
            text: {
                variant: "h6",
            },
            heading: {
                css: css`
                    display: none;
                `,
            },
        },
    },
};

export default cartPageStyles;
